import React, { useState, useEffect, useMemo } from 'react';
import { useDataProvider } from 'react-admin';
import { isDeepEqual } from 'remeda';
import {
  Box,
  FormControl,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Skeleton,
} from '@mui/material';
import { useInfiniteQuery, InfiniteData } from '@tanstack/react-query';
import { useIntersection } from '@mantine/hooks';
import { useInlineAsset } from '../context';
import {
  GetRelatedContentStylesResponse,
  RelatedContentStyle,
} from 'utility/types/dataProvider';
import { IntersectionLoader } from 'components/generic/IntersectionLoader';

type InlineRelatedContentSelectProps = {
  onSelect: (item: RelatedContentStyle) => void;
};

export const InlineRelatedContentSelect = ({
  onSelect,
}: InlineRelatedContentSelectProps) => {
  const dataProvider = useDataProvider();
  const { relContentStyles, setRelContentStyles, inlineRecord } = useInlineAsset();
  const initialStyle = inlineRecord?.style || '';

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<string>(initialStyle);
  const [menuWidth, setMenuWidth] = useState<number | undefined>(200);

  // Intersection observer for infinite scroll
  const { ref: intersectionRef, entry } = useIntersection({ threshold: 1 });

  // Fetch data with infinite query
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteQuery({
      queryKey: ['relatedContentStyles'],
      queryFn: async ({ pageParam }: { pageParam: number }) => {
        const response = await dataProvider.getRelatedContentStyles({
          params: { page: pageParam, limit: 50 },
        });
        return response;
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage: GetRelatedContentStylesResponse) =>
        lastPage.data.next ? parseInt(lastPage.data.next, 10) : undefined,
    });

  // Extract available options
  const availableOptions = useMemo((): RelatedContentStyle[] => {
    const infiniteData = data as
      | InfiniteData<GetRelatedContentStylesResponse>
      | undefined;
    if (!infiniteData?.pages) return [];
    return infiniteData.pages.flatMap((page) => page.data.items);
  }, [data]);

  /**
   * Store our relContentStyles in the context for use
   * Note: This is only needed at the moment because related_content items
   *       do not store their style ids in their payload for lookup in /editorial/relatedcontentstyle/{id}/
   *       In order to get style details like force_title for existing inline we use this data instead
   *       TNCMS-555719 (will allow for removal of this)
   */
  useEffect(() => {
    if (
      availableOptions.length > 0 &&
      !isDeepEqual(availableOptions, relContentStyles) // Using remeda's isDeepEqual
    ) {
      setRelContentStyles(availableOptions);
    }
  }, [availableOptions, relContentStyles, setRelContentStyles]);

  // Trigger fetching more data when the observer intersects
  useEffect(() => {
    if (entry?.isIntersecting && hasNextPage) {
      fetchNextPage();
    }
  }, [entry?.isIntersecting, hasNextPage, fetchNextPage]);

  // Open the menu
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
    setMenuWidth(event.currentTarget.clientWidth);
    setMenuOpen(true);
  };

  // Close the menu
  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuOpen(false);
  };

  // Handle item selection
  const handleItemSelect = (item: RelatedContentStyle) => {
    setSelectedItem(item.name); // Update displayed selection
    onSelect(item); // Pass selection to the parent
    handleMenuClose();
  };

  if (isLoading && !data)
    return (
      <Skeleton
        component={Box}
        variant="text"
        width="100%"
      />
    );

  return (
    <Box data-testid="inline-related-content-select">
      <FormControl
        variant="outlined"
        fullWidth
        required
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 0.5,
          }}
        >
          <InputLabel
            htmlFor="inline-related-content-select-input"
            sx={{
              position: 'relative',
              transform: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
              color: 'text.primary',
            }}
          >
            Style
          </InputLabel>
        </Box>
        <Select
          id="inline-related-content-select-input"
          open={false} // Using custom menu
          value={selectedItem || ''}
          displayEmpty
          onClick={handleMenuOpen}
          data-testid="inline-related-content-select-input"
          sx={{
            bgcolor: 'background.paper',
            mt: 1,
          }}
        >
          <MenuItem
            disabled
            value={selectedItem || ''}
          >
            {selectedItem || 'Select Style'}
          </MenuItem>
        </Select>
      </FormControl>

      {/* Infinite Scroll Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={handleMenuClose}
        slotProps={{
          paper: {
            sx: { width: menuWidth, maxHeight: 300, overflow: 'auto' },
          },
        }}
      >
        {availableOptions.length > 0 ? (
          availableOptions.map((item) => (
            <MenuItem
              key={`relcontent-${item.id}`}
              value={item.name}
              onClick={() => handleItemSelect(item)}
            >
              {item.name}
            </MenuItem>
          ))
        ) : (
          <MenuItem
            key="relcontent-no-options"
            disabled
          >
            No options available
          </MenuItem>
        )}

        <IntersectionLoader
          hasNextPage={hasNextPage}
          isFetching={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          ixRef={intersectionRef}
          entry={entry}
          polymorph="div"
        />
      </Menu>
    </Box>
  );
};

export default InlineRelatedContentSelect;
